import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faEye,
  faDownload,
  faUpload,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSousEtablissementById,
  createSousEtablissement,
  updateSousEtablissement,
} from "../../../services/api";
import { SousEtablissement } from "../../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "../AdminLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../../utils/media";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

interface FileWithPreview extends File {
  preview?: string;
}

const SousEtablissementForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<FileWithPreview | null>(null);
  const [existingLogo, setExistingLogo] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [arabicDesc, setArabicDesc] = useState("");
  const [frenchDesc, setFrenchDesc] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<SousEtablissement>();

  useEffect(() => {
    if (isEditMode) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          const response = await getSousEtablissementById(parseInt(id!));
          const data = response.data;

          reset({
            ...data,
          });
          setArabicDesc(data.descarabe || "");
          setFrenchDesc(data.descfrancais || "");
          if (data.logo) {
            setExistingLogo(data.logo);
          }
        } catch (error) {
          console.error("Error fetching sous-établissement:", error);
          toast.error(getTranslation("fetchError", language));
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [id, isEditMode, reset]);

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files && files[0] && files[0].type.startsWith("image/")) {
        const file = files[0] as FileWithPreview;
        file.preview = URL.createObjectURL(file);
        setLogoFile(file);
      }
      setIsDragOver(false);
    },
    []
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      e.target.files &&
      e.target.files[0] &&
      e.target.files[0].type.startsWith("image/")
    ) {
      const file = e.target.files[0] as FileWithPreview;
      file.preview = URL.createObjectURL(file);
      setLogoFile(file);
    }
  };

  const removeFile = () => {
    setLogoFile(null);
    if (logoFile?.preview) {
      URL.revokeObjectURL(logoFile.preview);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleViewLogo = () => {
    if (existingLogo) {
      const imageWindow = window.open();
      if (imageWindow) {
        imageWindow.location.href = resolveMediaUrl(existingLogo);
      }
    }
  };

  const handleDownloadLogo = () => {
    if (existingLogo) {
      const link = document.createElement("a");
      link.href = resolveMediaUrl(existingLogo);
      link.download = getFileName(existingLogo) || "logo.jpg";
      link.click();
    }
  };

  const encodeFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: SousEtablissement) => {
    setIsLoading(true);
    try {
      let logoValue: string | null = null;

      if (logoFile) {
        logoValue = await encodeFileContent(logoFile);
      } else if (existingLogo && isEditMode) {
        logoValue = getFileName(existingLogo) || existingLogo;
      }

      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        descarabe: arabicDesc,
        descfrancais: frenchDesc,
        lien: data.lien,
        logo: logoValue,
      };

      if (isEditMode) {
        await updateSousEtablissement(parseInt(id!), payload);
        toast.success("تم تحديث المؤسسة الفرعية بنجاح");
      } else {
        await createSousEtablissement(payload);
        toast.success("تمت إضافة المؤسسة الفرعية بنجاح");
      }

      navigate("/admin/sousetablissements");
    } catch (error) {
      console.error("Error saving sous-établissement:", error);
      toast.error(getTranslation("saveError", language));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (logoFile?.preview) {
        URL.revokeObjectURL(logoFile.preview);
      }
    };
  }, [logoFile]);

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor {
            direction: rtl;
            text-align: right;
          }
          
          .rtl-quill .ql-editor ol, 
          .rtl-quill .ql-editor ul {
            padding-right: 1.5em;
            padding-left: 0;
          }
          
          .rtl-quill .ql-editor li:before {
            margin-left: 0.3em;
            margin-right: -1.5em;
          }
          
          .ltr-quill .ql-editor {
            direction: ltr;
            text-align: left;
          }
          
          .ltr-quill .ql-editor ol, 
          .ltr-quill .ql-editor ul {
            padding-left: 1.5em;
            padding-right: 0;
          }
        `}
      </style>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode ? "تعديل المؤسسة الفرعية" : "إضافة مؤسسة فرعية جديدة"}
        </h1>
        <button
          onClick={() => navigate("/admin/sousetablissements")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />{getTranslation("back", language)}</button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                العنوان (عربي) *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                العنوان (فرنسي) *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Link */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="lien">
              رابط الموقع
            </label>
            <div className="relative">
              <input
                id="lien"
                type="url"
                {...register("lien")}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green"
                placeholder="https://www.example.com"
              />
              <FontAwesomeIcon
                icon={faLink}
                className="absolute right-3 top-3 text-gray-400"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              اتركه فارغاً إذا لم يكن هناك رابط
            </p>
          </div>

          {/* Arabic Description */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">الوصف (عربي)</label>
            <div className="rtl-quill">
              <ReactQuill
                theme="snow"
                value={arabicDesc}
                onChange={setArabicDesc}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* French Description */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">الوصف (فرنسي)</label>
            <div className="ltr-quill">
              <ReactQuill
                theme="snow"
                value={frenchDesc}
                onChange={setFrenchDesc}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* Logo Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">شعار المؤسسة</label>

            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? "border-mauritania-green bg-mauritania-green-light"
                  : "border-gray-300 hover:border-mauritania-green"
              }`}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() =>
                document.getElementById("logo-file-input")?.click()
              }
            >
              <input
                id="logo-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {logoFile ? (
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <img
                      src={logoFile.preview}
                      alt="Preview"
                      className="h-32 w-32 object-contain rounded-md mb-2"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile();
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1 truncate max-w-full">
                    {logoFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(logoFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : existingLogo ? (
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <img
                      src={resolveMediaUrl(existingLogo)}
                      alt="Existing"
                      className="h-32 w-32 object-contain rounded-md mb-2"
                    />
                  </div>
                  <p className="text-sm font-medium text-green-700 mb-2">
                    شعار موجود
                  </p>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewLogo();
                      }}
                      className="text-mauritania-green hover:text-mauritania-green-dark text-sm"
                    >
                      <FontAwesomeIcon icon={faEye} className="ml-1" />{getTranslation("preview", language)}</button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadLogo();
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FontAwesomeIcon icon={faDownload} className="ml-1" />{" "}
                      {getTranslation("download", language)}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                    <FontAwesomeIcon
                      icon={faUpload}
                      className="text-gray-400 text-2xl"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    اسحب الشعار هنا أو انقر للاختيار
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    الشعار الأمثل: 200×200 بكسل، PNG أو JPG
                  </p>
                </div>
              )}
            </div>

            {isEditMode && !logoFile && (
              <p className="text-sm text-gray-500 mt-2">
                اتركه فارغاً للحفاظ على الشعار الحالي
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading ? getTranslation("saving", language) : getTranslation("save", language)}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SousEtablissementForm;
